import React, { useState, useEffect } from 'react';
import { styled } from 'linaria/react';

// Import images
import logotypImage from './logotyp.png';
import ikonerImage from './ikoner.png';
import emmaliImage from './emmali.jpg';

// Styled components
const Container = styled.div`
  max-width: 1000px;
  margin: 30px auto;
  padding: 20px 20px 20px 0;
  font-family: 'Figtree', sans-serif;
  background-color: #ffffff;
  color: #333;
  opacity: 0;
  animation: fadeIn 0.8s ease-in-out forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const LogoContainer = styled.div`
  margin-bottom: 50px;
  opacity: 0;
  animation: slideInFromTop 0.6s ease-out 0.2s forwards;

  @keyframes slideInFromTop {
    from {
      opacity: 0;
      transform: translateY(-30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Logo = styled.img`
  height: 40px;
`;

const ProductImageSection = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;
  text-align: left;
  margin-bottom: 30px;
  opacity: 0;
  animation: slideInFromLeft 0.8s ease-out 0.4s forwards;

  @keyframes slideInFromLeft {
    from {
      opacity: 0;
      transform: translateX(-50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: left;
    text-align: left;

    > div {
      width: 100%;
    }

    img {
      width: 100%;
      height: auto;
    }

    h1,
    p,
    span {
      text-align: left !important;
    }
  }
`;

const ProductImage = styled.img`
  max-width: 100%;
  height: auto;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const ProductTitle = styled.h1`
  font-size: 24px;
  font-family: 'Figtree', sans-serif;
  text-align: left;
  margin-bottom: 15px;
`;

const ProductDescription = styled.p`
  font-size: 16px;
  line-height: 1.4;
  font-family: 'Figtree', sans-serif;
  text-align: left;
  margin-bottom: 20px;
`;

const FeaturesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  opacity: 0;
  animation: fadeInUp 0.5s ease-out forwards;
  animation-delay: ${props => props.delay}s;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const FeatureText = styled.span`
  font-size: 16px;
  font-family: 'Figtree', sans-serif;
`;

const AccordionContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
  opacity: 0;
  animation: slideInFromBottom 0.8s ease-out 0.8s forwards;

  @keyframes slideInFromBottom {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const AccordionItem = styled.div`
  border-top: 1px solid #000;
  padding: 30px 0;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(14, 58, 35, 0.02);
  }

  &:last-child {
    border-bottom: 1px solid #000;
  }

  &:first-child {
    grid-column: 1 / -1;
  }
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 22px;
  font-weight: 700;
  color: #0E3A23;
  transition: color 0.2s ease;

  ${AccordionItem}:hover & {
    color: #0E3A23;
  }
`;

const PlusSign = styled.span`
  font-size: 20px;
  transition: transform 0.3s ease;
  transform: ${props => props.isActive ? 'rotate(45deg)' : 'rotate(0deg)'};
`;

const AccordionContent = styled.div`
  max-height: ${props => props.isActive ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  padding-top: ${props => props.isActive ? '10px' : '0'};
  font-weight: normal;
  opacity: ${props => props.isActive ? '1' : '0'};
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), 
              padding-top 0.4s cubic-bezier(0.4, 0, 0.2, 1), 
              opacity 0.3s ease;
`;

const InfoGrid = styled.div`
  display: flex;
  gap: 40px;
  flex-wrap: wrap;
`;

const InfoColumn = styled.div`
  flex: 1;
  min-width: 300px;
`;

const InfoParagraph = styled.p`
  font-size: 16px;
  line-height: 1.4;
  font-family: 'Figtree', sans-serif;
  text-align: left;
  margin-bottom: 8px;
`;

const CareInstructions = styled.div`
  margin-top: 10px;
`;

const CareImage = styled.img`
  height: 40px;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const UnorderedList = styled.ul`
  margin: 0;
  padding-left: 20px;
`;

const ListItem = styled.li`
  font-size: 16px;
  line-height: 1.4;
  font-family: 'Figtree', sans-serif;
  margin-bottom: 4px;
`;

// SVG Icons as React components
const MobilePocketIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <rect x="7" y="2" width="10" height="20" rx="2"/>
    <circle cx="12" cy="18" r="1"/>
  </svg>
);

const FunctionalMaterialIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06A1.65 1.65 0 0015 19.4a1.65 1.65 0 00-1.82-.33l-.12.05a1.65 1.65 0 01-1.22 0l-.12-.05A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.65 1.65 0 004.6 15a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 8.6c.23-.1.47-.17.72-.2a1.65 1.65 0 011.22 0c.25.03.49.1.72.2a1.65 1.65 0 002.18-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 00-.33 1.82z"/>
  </svg>
);

const WashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M4 7l1.5 12h13L20 7M9 11v6M15 11v6"/>
  </svg>
);

const EmmaliPage = () => {
  const [activeAccordion, setActiveAccordion] = useState(null);

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const accordionData = [
    {
      title: 'Product information',
      content: (
        <InfoGrid>
          <InfoColumn>
            <InfoParagraph><strong>Article name:</strong> Ridleggings Emmali CRW®</InfoParagraph>
            <InfoParagraph><strong>Item number:</strong> 100655-10-XS</InfoParagraph>
            <InfoParagraph><strong>Colour:</strong> Black</InfoParagraph>
            <InfoParagraph><strong>Size:</strong> EU XS</InfoParagraph>
            <InfoParagraph><strong>Size range:</strong> XXS-XXL</InfoParagraph>
            <InfoParagraph><strong>EAN:</strong> 7315470284397</InfoParagraph>
            <InfoParagraph><strong>Year:</strong> 2025</InfoParagraph>
            <InfoParagraph><strong>Season:</strong> Spring/Summer</InfoParagraph>
            <InfoParagraph><strong>Age group:</strong> Adults</InfoParagraph>
            <InfoParagraph><strong>Gender:</strong> Women</InfoParagraph>
          </InfoColumn>
          <InfoColumn>
            <InfoParagraph><strong>Hang tag:</strong> CRW hang tag, 100% paper, ?</InfoParagraph>
            <InfoParagraph><strong>Product weight:</strong> 0,214kg</InfoParagraph>
            <InfoParagraph><strong>Packaging weight:</strong> 0,015kg</InfoParagraph>
            <InfoParagraph><strong>Recycled content:</strong> No</InfoParagraph>
            <InfoParagraph><strong>Harmful substances:</strong> No</InfoParagraph>
            <InfoParagraph><strong>Release of microplastics:</strong> No</InfoParagraph>
            <InfoParagraph><strong>Market segment:</strong> Premium</InfoParagraph>
            <InfoParagraph><strong>Order number:</strong> 321601-0</InfoParagraph>
            <InfoParagraph><strong>HS code:</strong> 6104630000</InfoParagraph>
            <InfoParagraph><strong>Manufacturer:</strong> Hööks Hästsport AB, 501 77 Borås</InfoParagraph>
          </InfoColumn>
        </InfoGrid>
      )
    },
    {
      title: 'Care',
      content: (
        <div>
          <InfoParagraph>
            <strong>Care and Repair:</strong><br />
            Take good care of your clothes in order to give them the longest possible life. Avoid unnecessary washing and do not use fabric softener. If your clothes get damaged, repair them instead of throwing them away. Please find useful tips at our webpage, Hööks Academy. Consider passing your clothes to someone else if they are no longer of any use for you.
          </InfoParagraph>
          <CareInstructions>
            <InfoParagraph style={{ marginBottom: '10px' }}>
              <strong>Care instruction:</strong> Wash with similar colours
            </InfoParagraph>
            <CareImage src={ikonerImage} alt="Care instruction symbols" />
          </CareInstructions>
        </div>
      )
    },
    {
      title: 'Materials',
      content: (
        <div>
          <InfoParagraph><strong>Fabric:</strong> Knitted stretch fabric, 80% polyester, 20% elastan, ?</InfoParagraph>
          <InfoParagraph><strong>Thread:</strong> Sewing thread, 100% polyester, ?</InfoParagraph>
          <InfoParagraph><strong>Elastic:</strong> Elastic band, 80% polyester, 20% elastan, ?</InfoParagraph>
          <InfoParagraph><strong>Label:</strong> CRW main label, 100% polyester, ?</InfoParagraph>
          <InfoParagraph><strong>Label:</strong> CRW size label, 100% polyester, ?</InfoParagraph>
          <InfoParagraph><strong>Material certification:</strong> Fabric: OEKO-TEX® Standard 100 certified. Cert nr: BJ015 224672</InfoParagraph>
        </div>
      )
    },
    {
      title: 'Company and brand information',
      content: (
        <InfoColumn>
          <InfoParagraph><strong>Brand:</strong> CRW®</InfoParagraph>
          <InfoParagraph><strong>Company:</strong> Hööks Hästsport AB</InfoParagraph>
          <InfoParagraph><strong>Adress:</strong> Hööks Hästsport AB, 501 77 Borås</InfoParagraph>
          <InfoParagraph><strong>Contact:</strong> contact@hooks.se</InfoParagraph>
        </InfoColumn>
      )
    },
    {
      title: 'Supply Chain',
      content: (
        <div>
          <InfoParagraph><strong>Manufacturing facility:</strong> Jiangyin Youjin Garment Co., Ltd., China</InfoParagraph>
          <InfoParagraph><strong>Fabric:</strong></InfoParagraph>
          <UnorderedList>
            <ListItem>Country of Origin - Confection: China</ListItem>
            <ListItem>Country of Origin - Dyeing and Printing: China</ListItem>
            <ListItem>Country of Origin - Knitting: China</ListItem>
          </UnorderedList>
          <InfoParagraph><strong>Thread:</strong></InfoParagraph>
          <UnorderedList>
            <ListItem>Country of Origin - Confection: China</ListItem>
            <ListItem>Country of Origin - Dyeing: China</ListItem>
            <ListItem>Country of Origin - Spinning: China</ListItem>
          </UnorderedList>
          <InfoParagraph><strong>Elastic:</strong></InfoParagraph>
          <UnorderedList>
            <ListItem>Country of Origin - Confection: China</ListItem>
            <ListItem>Country of Origin - Dyeing: China</ListItem>
            <ListItem>Country of Origin - Knitting: China</ListItem>
          </UnorderedList>
          <InfoParagraph><strong>Label:</strong></InfoParagraph>
          <UnorderedList>
            <ListItem>Country of Origin - Confection: China</ListItem>
            <ListItem>Country of Origin - Dyeing: China</ListItem>
            <ListItem>Country of Origin - Weaving: China</ListItem>
          </UnorderedList>
          <InfoParagraph><strong>Hang tag:</strong></InfoParagraph>
          <UnorderedList>
            <ListItem>Country of Origin - XXX: China</ListItem>
            <ListItem>Country of Origin - XXX: China</ListItem>
            <ListItem>Country of Origin - XXX: China</ListItem>
          </UnorderedList>
        </div>
      )
    },
    {
      title: 'Circularity & Sustainability',
      content: (
        <div>
          <InfoParagraph><strong>Performance:</strong></InfoParagraph>
          <UnorderedList>
            <ListItem>Colour fastness to washing 4 (scale 1–5)</ListItem>
            <ListItem>Colour fastness to rubbing (dry) 4 (scale 1–5)</ListItem>
            <ListItem>Colour fastness to rubbing (wet) 4 (scale 1–5)</ListItem>
            <ListItem>Pilling resistance 4 (scale 1–5)</ListItem>
          </UnorderedList>
          <InfoParagraph>
            <strong>Recycling instructions:</strong><br />
            Make sure to leave clothes at your local collection system and sort as textile waste. Do not throw clothes in household waste.
          </InfoParagraph>
          <InfoParagraph>
            <strong>Corporate Social Responsibility:</strong><br />
            Product manufacturing unit is audited according to Amfori Code of Conduct.<br />
            Read more about Amfori here: https://www.amfori.org/en/about/who-we-are/about-amfori
          </InfoParagraph>
          <InfoParagraph>
            <strong>Sustainability at Hööks:</strong><br />
            Read more about our sustainability work at our webpage:<br />
            https://www.hooks.se/hallbarhetsarbete
          </InfoParagraph>
          <InfoParagraph><strong>Quantified Environmental Footprint:</strong> ?</InfoParagraph>
          <InfoParagraph><strong>Use or consumption of energy, water and other resources:</strong> ?</InfoParagraph>
          <InfoParagraph><strong>Carbon Footprint:</strong> ?</InfoParagraph>
          <InfoParagraph><strong>Material Footprint:</strong> ?</InfoParagraph>
          <InfoParagraph><strong>Emissions to air, water or soil:</strong> ?</InfoParagraph>
          <InfoParagraph><strong>Waste type:</strong> Non Hazardous Waste</InfoParagraph>
        </div>
      )
    }
  ];

  return (
    <Container>
      <LogoContainer>
        <Logo src={logotypImage} alt="Företagslogga" />
      </LogoContainer>
      
      <ProductImageSection>
        <div style={{ flex: 1 }}>
          <ProductImage src={emmaliImage} alt="Produktbild - Emmali Ridtights" />
        </div>
        
        <ProductInfo>
          <ProductTitle>Ridleggings Emmali CRW®</ProductTitle>
          <ProductDescription>
            Stylish riding leggings with a high waist. The leggings are soft and stretchy and made from a lighter-weight technical fabric that is very comfortable to wear. Full seat with grip for security in the saddle. Two practical phone pockets on the outside of the thigh. Stretchy fabric at the ankles.
          </ProductDescription>
          
          <FeaturesContainer>
            <FeatureItem delay={0.6}>
              <MobilePocketIcon />
              <FeatureText>&nbsp;Mobile pocket</FeatureText>
            </FeatureItem>
            <FeatureItem delay={0.8}>
              <FunctionalMaterialIcon />
              <FeatureText>&nbsp;Functional material</FeatureText>
            </FeatureItem>
            <FeatureItem delay={1.0}>
              <WashIcon />
              <FeatureText>&nbsp;Wash 40°</FeatureText>
            </FeatureItem>
          </FeaturesContainer>
        </ProductInfo>
      </ProductImageSection>

      <AccordionContainer>
        {accordionData.map((item, index) => (
          <AccordionItem key={index} onClick={() => toggleAccordion(index)}>
            <AccordionHeader>
              {item.title} <PlusSign isActive={activeAccordion === index}>{activeAccordion === index ? '-' : '+'}</PlusSign>
            </AccordionHeader>
            <AccordionContent isActive={activeAccordion === index}>
              {item.content}
            </AccordionContent>
          </AccordionItem>
        ))}
      </AccordionContainer>
    </Container>
  );
};

export default EmmaliPage;